import { Button, FormTextField, Icon, TokenSelect } from "@/components";
import type { ControllerRenderProps, FieldError } from "react-hook-form";
import { useCallback, useEffect, useMemo, type ChangeEvent } from "react";
import type { TokenFormValues, TOKEN_AMOUNT_KEY } from "@/types";
import type { TokenId } from "@/types";
import { TOKEN_CONFIG } from "@/config";
import { z } from "zod";
import { memo } from "react";
import { Seperator } from "../shared/Seperator";
import { useWalletBalances } from "@/hooks";
import { formatDisplayCurrency } from "@/utils/format-currency";

const getMaxDecimals = (selectedTokenId?: TokenId) => {
    if (!selectedTokenId) return 6;
    return TOKEN_CONFIG[selectedTokenId]?.decimals ?? 6; // Fallback if decimals is undefined
};

const validateInput = (maxDecimals: number): z.ZodSchema<string> =>
    z
        .string()
        .transform((val) => {
            if (val.startsWith(".")) {
                return `0${val}`;
            }
            return val;
        })
        .refine(
            (input) => {
                if (!input) return true;
                if (input.includes("-")) return false;

                const regex = /^\d*\.?\d*$/;
                if (!regex.test(input)) return false;

                if (input.split(".").length > 2) return false;

                const parts = input.split(".");
                if (parts.length === 2 && parts[1].length > maxDecimals) return false;

                return true;
            },
            {
                message: "Invalid input format",
            },
        );

interface TokenInputProps {
    amount: ControllerRenderProps<TokenFormValues, typeof TOKEN_AMOUNT_KEY>;
    amountError?: FieldError;
    selectedTokenId: TokenId;
    handleTokenSelect: (tokenId: TokenId) => void;
    handleMaxClick: () => void;
    tokenOptions: TokenId[];
    label?: string;
    overrideBalance?: string;
    isMaxDisabled?: boolean;
    walletBalanceOverride?: {
        amount: string;
        balancePrefix: string;
    };
}

interface UseTokenInputProps {
    amount: ControllerRenderProps<TokenFormValues, typeof TOKEN_AMOUNT_KEY>;
    selectedTokenId: TokenId;
}

function useTokenInput({ amount, selectedTokenId }: UseTokenInputProps) {
    const maxDecimals = useMemo(() => getMaxDecimals(selectedTokenId), [selectedTokenId]);

    const inputSchema = useMemo(() => validateInput(maxDecimals), [maxDecimals]);

    const handleAmountChanged = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;

            // Allow empty input
            if (value === "") {
                amount.onChange("");
                return;
            }

            const result = inputSchema.safeParse(value);

            if (result.success) {
                amount.onChange(result.data);
            } else {
                // Keep the previous valid value instead of silently ignoring
                console.warn("Input validation failed:", result.error);
            }
        },
        [amount, inputSchema],
    );

    useEffect(() => {
        // When user changes tokenId, set current amount to ""
        amount.onChange("");
        // do not add `amount` as dep; it will force re-render
    }, [selectedTokenId]);

    return {
        maxDecimals,
        handleAmountChanged,
    };
}

export const TokenInput = memo(function TokenInput({
    amount,
    amountError,
    selectedTokenId,
    handleTokenSelect,
    handleMaxClick,
    tokenOptions,
    label = "Enter amount",
    isMaxDisabled,
    walletBalanceOverride,
}: TokenInputProps) {
    const { isLoading, getWalletTokenBalance } = useWalletBalances();

    const { handleAmountChanged } = useTokenInput({
        amount,
        selectedTokenId,
    });

    const walletBalance = useMemo(() => {
        if (isLoading) return "";
        if (walletBalanceOverride?.amount) return walletBalanceOverride.amount;
        return getWalletTokenBalance(selectedTokenId).tokenBalance;
    }, [getWalletTokenBalance, isLoading, selectedTokenId, walletBalanceOverride]);

    const walletBalanceDisplay = isLoading ? <>&mdash;</> : formatDisplayCurrency(walletBalance);

    const isWalletBalanceZero = useMemo(() => {
        if (isLoading) return false;
        const parsedWalletBalance = Number(walletBalance);
        if (!Number.isFinite(parsedWalletBalance)) return true;
        return parsedWalletBalance <= 0;
    }, [isLoading, walletBalance]);
    const isMaxButtonDisabled = isMaxDisabled ?? isWalletBalanceZero;

    const maxButtonOnClick = useCallback(() => {
        handleMaxClick();
    }, [handleMaxClick]);

    const formField = useMemo(
        () => ({
            ...amount,
            onChange: handleAmountChanged,
        }),
        [amount, handleAmountChanged],
    );

    const hasValue = Boolean(amount.value);

    return (
        <div className="group flex flex-col gap-2">
            <div className="text-sm text-primary -mb-1">{label}</div>
            <div
                className={`rounded-lg border-[2px] bg-input-bg px-4 py-4 flex gap-3 flex-col ${
                    amountError
                        ? "border-red-500"
                        : hasValue
                          ? "border-primary"
                          : "border-primary/20 group-focus-within:border-primary"
                }`}
            >
                <FormTextField
                    field={formField}
                    data-testid="form-input"
                    error={amountError}
                    placeholder="0.00"
                    autoFocus={true}
                    endAdornment={
                        <TokenSelect value={selectedTokenId} onChange={handleTokenSelect} options={tokenOptions} />
                    }
                />
                <Seperator />
                <div className="flex justify-between my-0 items-center">
                    <Button size="xs" disabled={isMaxButtonDisabled} onClick={maxButtonOnClick}>
                        MAX
                    </Button>
                    <span className="text-[12px] text-dark font-normal whitespace-nowrap select-none">
                        {walletBalanceOverride?.balancePrefix ?? "Wallet balance: "}
                        <span className="text-primary">
                            {walletBalanceDisplay} {TOKEN_CONFIG[selectedTokenId]?.symbol}
                        </span>
                    </span>
                </div>
            </div>
            {amountError && (
                <div className="flex items-center gap-1 text-red-500 text-sm mt-1">
                    <Icon name="Info" className="w-4 h-4" />
                    <div>{amountError.message}</div>
                </div>
            )}
        </div>
    );
});

TokenInput.displayName = "TokenInput";
